import { useEffect, useState } from 'react';
import {
    AlertBannerType,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
} from '@boomi/exosphere';
import CopyField from '../shared/CopyField';
import {
    type IdentityProvider,
    fetchIdentityProviders,
} from '../identity-providers/identityProviders';
import { getConnector, type Connector } from '../connectors/connectors';
import { createConnection, type CreatedConnection } from './connections';
import ConnectionFormFields, { type FormValues, EMPTY_FORM_VALUES } from './ConnectionFormFields';
import ConnectorSidebar from './ConnectorSidebar';
import styles from '../shared/Form.module.css';
import connectionStyles from './Connection.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

interface ConnectionCreateProps {
    connectorId: string;
    onClose: () => void;
}

const ConnectionCreate = ({ connectorId, onClose }: ConnectionCreateProps) => {
    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);
    const [connector, setConnector] = useState<Connector | null>(null);
    const [identityProviders, setIdentityProviders] = useState<IdentityProvider[]>([]);
    const [isLoadingIdps, setIsLoadingIdps] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createdConnection, setCreatedConnection] = useState<CreatedConnection | null>(null);

    useEffect(() => {
        Promise.all([fetchIdentityProviders(), getConnector(connectorId)])
            .then(([idpResponse, connectorData]) => {
                setIdentityProviders(idpResponse.results);
                setConnector(connectorData);
            })
            .finally(() => setIsLoadingIdps(false));
    }, [connectorId]);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setError(null);
        setSubmitting(true);

        try {
            const result = await createConnection({
                name: values.name,
                identityProviderId: values.identityProviderId || null,
                connectorId,
                options: values.userOptions,
                enabled: values.enabled,
                accountOwned: true,
            });
            setCreatedConnection(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    if (createdConnection) {
        return (
            <>
                <div className={connectionStyles.drawerLayout}>
                    {connector && <ConnectorSidebar connector={connector} />}
                    <div className={connectionStyles.formColumn}>
                        <ExAlertBanner type={AlertBannerType.WARNING} open>
                            Store the client secret safely — it will not be shown again.
                        </ExAlertBanner>

                        <div className={styles.credentials}>
                            <CopyField
                                label="MCP URL"
                                value={`${window.location.origin}/mcp/${connectorId}`}
                            />
                            <CopyField label="Client ID" value={createdConnection.clientId} />
                            <CopyField
                                label="Client Secret"
                                value={createdConnection.clientSecret}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.actions} slot="footer">
                    <ExButton
                        type={ButtonType.PRIMARY}
                        flavor={ButtonFlavor.BRANDED}
                        onClick={onClose}
                    >
                        Done
                    </ExButton>
                </div>
            </>
        );
    }

    return (
        <>
            <div className={connectionStyles.drawerLayout}>
                {connector && <ConnectorSidebar connector={connector} />}
                <div className={connectionStyles.formColumn}>
                    <form className={styles.form} onSubmit={handleSubmit}>
                        {connector && (
                            <ConnectionFormFields
                                values={values}
                                identityProviders={identityProviders}
                                isLoadingIdentityProviders={isLoadingIdps}
                                onChange={handleChange}
                                userAuthOptions={connector.userAuthOptions}
                                appAuthOptions={connector.appAuthOptions}
                                boomiSuppliesAppAuthOptions={connector.boomiSuppliesAppAuthOptions}
                                connectorName={connector.name}
                                connectorId={connectorId}
                            />
                        )}

                        {error && (
                            <ExAlertBanner
                                className={styles.saveError}
                                type={AlertBannerType.ERROR}
                                open
                                onClose={() => setError(null)}
                            >
                                {error}
                            </ExAlertBanner>
                        )}
                    </form>
                </div>
            </div>
            <div className={styles.actions} slot="footer">
                <ExButton
                    type={ButtonType.PRIMARY}
                    flavor={ButtonFlavor.BRANDED}
                    disabled={submitting}
                    onClick={() => handleSubmit()}
                >
                    {submitting ? 'Adding…' : 'Add config'}
                </ExButton>
            </div>
        </>
    );
};

export default ConnectionCreate;
